import React, { MutableRefObject, useEffect, useMemo, useRef, useState } from "react";

import { TFunction, formatNumber, getCenter, useDebouncedState, useDocScanFieldsPaginated, useTranslation } from "utils";

import { DocFieldRaw, DocScanFieldRecord, DocScanJobDetail } from "models";

import {
  AlertBanner,
  Button,
  FieldMatchCard,
  Flex,
  InputSearch,
  MapStatic,
  Modal,
  PLFieldMatchCard,
  PageSpin,
  SelectableImageCard,
  SelectableImageCardProps,
  Typography,
  getDocFieldBoundaries,
  getDocFieldCrops,
  getDocFieldValues,
} from "components";

// Matches the "document" legend color used elsewhere for PL parcel polygons (pl-field-match-card.scss's
// $pl-document-color / MapStatic's secondaryGeometry color) — candidates here are all document-side parcels.
const DOCUMENT_COLOR = "0xFF8C00FF";
const DOCUMENT_FILL_COLOR = "0xFF8C0033";

export type ReviewStepMode = "match" | "need_review";

type ReviewStepProps = {
  docScan: DocScanJobDetail;
  mode: ReviewStepMode;
  selectedFieldIds: Set<string>;
  onSelectedFieldIdsChange: React.Dispatch<React.SetStateAction<Set<string>>>;
  matchOverrides: Map<string, DocScanFieldRecord>;
  onMatchOverridesChange: React.Dispatch<React.SetStateAction<Map<string, DocScanFieldRecord>>>;
  seenFieldRecords: MutableRefObject<Map<string, DocScanFieldRecord>>;
  selectAllActiveRef: MutableRefObject<boolean>;
  isPoland?: boolean;
};

export const ReviewStep = ({
  docScan,
  mode,
  selectedFieldIds,
  onSelectedFieldIdsChange,
  matchOverrides,
  onMatchOverridesChange,
  seenFieldRecords,
  selectAllActiveRef,
  isPoland,
}: ReviewStepProps) => {
  const { t } = useTranslation("docScans");

  const { loadedFields, isFetching, isError, sentinelRef } = useDocScanFieldsPaginated({
    docScanId: docScan.id,
    overallStatus: mode,
    limit: mode === "match" ? 100 : undefined,
    onNewFields: (fields) => {
      onSelectedFieldIdsChange((prev) => {
        const newFields = fields.filter((f) => !seenFieldRecords.current.has(f.id));
        newFields.forEach((f) => seenFieldRecords.current.set(f.id, f));
        if (newFields.length === 0) return prev;
        if (!selectAllActiveRef.current) return prev;
        const next = new Set(prev);
        newFields.forEach((f) => next.add(f.id));
        return next;
      });
    },
  });

  const handleFieldToggle = (fieldId: string) => {
    onSelectedFieldIdsChange((prev) => {
      const next = new Set(prev);
      if (next.has(fieldId)) {
        next.delete(fieldId);
      } else {
        next.add(fieldId);
      }
      return next;
    });
  };

  const handleChangeMatch = (fieldId: string, newMatch: DocScanFieldRecord) => {
    onMatchOverridesChange((prev) => new Map(prev).set(fieldId, newMatch));
  };

  const hasAnySelected = loadedFields.some((f) => selectedFieldIds.has(f.id));

  useEffect(() => {
    if (loadedFields.length > 0 && !hasAnySelected) selectAllActiveRef.current = false;
  }, [hasAnySelected]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSelectAll = () => {
    selectAllActiveRef.current = true;
    onSelectedFieldIdsChange((prev) => {
      const next = new Set(prev);
      loadedFields.forEach((f) => next.add(f.id));
      return next;
    });
  };

  const handleDeselectAll = () => {
    selectAllActiveRef.current = false;
    onSelectedFieldIdsChange((prev) => {
      const next = new Set(prev);
      loadedFields.forEach((f) => next.delete(f.id));
      return next;
    });
  };

  const selectDeselectButton = loadedFields.length > 0 && (
    <Button type="link" onClick={hasAnySelected ? handleDeselectAll : handleSelectAll} dataTestId="select-deselect-all">
      {hasAnySelected ? t("review.deselectAll") : t("review.selectAll")}
    </Button>
  );

  return (
    <div className="doc-scan-content wide">
      <Typography variant="h1">{t(`review.${mode}.heading`)}</Typography>
      <Typography variant="paragraph-m" className="margin-top-100 margin-bottom-100">
        {t(`review.${mode}.subtext`)}
      </Typography>

      {mode === "need_review" ? (
        <Flex justify="space-between" align="center" gap={16} className="margin-top-25 margin-bottom-100">
          <AlertBanner
            dataTestId="review-step-banner"
            type="warning"
            iconName="tabler/alert-circle"
            iconSize={24}
            iconAlign="start"
            description={t("review.need_review.banner")}
            descriptionSize="paragraph-m"
          />
          {selectDeselectButton}
        </Flex>
      ) : (
        <Flex justify="flex-end" className="margin-bottom-100">
          {selectDeselectButton}
        </Flex>
      )}

      {isError && (
        <Typography variant="paragraph-s" color="grey-600" className="margin-top-200">
          {t("review.errorLoadingFields")}
        </Typography>
      )}

      <div className={isPoland ? "doc-scan-field-grid" : undefined}>
        {loadedFields.map((record) => {
          const overriddenMatch = matchOverrides.get(record.id);

          return (
            <FieldCard
              key={record.id}
              record={record}
              overriddenDocField={overriddenMatch?.docFieldRaw ?? null}
              overriddenMatchId={overriddenMatch?.id ?? null}
              isSelected={selectedFieldIds.has(record.id)}
              onToggle={() => handleFieldToggle(record.id)}
              onChangeMatch={(newMatch) => handleChangeMatch(record.id, newMatch)}
              docScanId={docScan.id}
              isPoland={isPoland}
              t={t}
            />
          );
        })}
      </div>

      {!isFetching && !isError && loadedFields.length === 0 && (
        <Typography variant="paragraph-s" color="grey-600" className="margin-top-200">
          {t("review.noFieldsFound")}
        </Typography>
      )}

      {isFetching && <PageSpin />}

      <div ref={sentinelRef} style={{ height: 8 }} />
    </div>
  );
};

const PLFieldPickerRow = ({
  candidate,
  isCurrent,
  checked,
  onChange,
  t,
}: {
  candidate: DocScanFieldRecord;
  isCurrent: boolean;
  checked: boolean;
  onChange: SelectableImageCardProps["onChange"];
  t: TFunction;
}) => {
  const [showModal, setShowModal] = useState(false);
  // A group has no values of its own: draw each parcel's boundary and list each parcel's values.
  const { parcelBoundary, otherParcelBoundaries } = useMemo(() => {
    const [first, ...rest] = getDocFieldBoundaries(candidate.docFieldRaw);
    return { parcelBoundary: first, otherParcelBoundaries: rest };
  }, [candidate.docFieldRaw]);
  const parcelIds = getDocFieldValues(candidate.docFieldRaw, (f) => f.flik_number).join(", ") || "-";
  const center = parcelBoundary?.length ? getCenter([parcelBoundary, ...otherParcelBoundaries].flat()) : undefined;

  return (
    <div className="pl-field-picker-row">
      {center && parcelBoundary?.length && (
        <>
          <button type="button" className="pl-field-picker-row-map" onClick={() => setShowModal(true)} data-testid={`field-picker-map-${candidate.id}`}>
            <MapStatic
              geometry={{ geometry: parcelBoundary }}
              extraGeometries={otherParcelBoundaries}
              location={center}
              primaryColor={DOCUMENT_COLOR}
              primaryFillColor={DOCUMENT_FILL_COLOR}
              zoomBoost={0}
            />
          </button>
          <Modal
            dataTestId={`field-picker-map-modal-${candidate.id}`}
            showModal={showModal}
            onCancel={() => setShowModal(false)}
            maskClosable
            footer={null}
            title={parcelIds}
            content={
              <div className="pl-field-picker-row-modal-map">
                <MapStatic
                  geometry={{ geometry: parcelBoundary }}
                  extraGeometries={otherParcelBoundaries}
                  location={center}
                  primaryColor={DOCUMENT_COLOR}
                  primaryFillColor={DOCUMENT_FILL_COLOR}
                  zoomable
                  pannable
                  zoomBoost={1}
                />
              </div>
            }
          />
        </>
      )}
      <SelectableImageCard
        dataTestId={`field-picker-card-${candidate.id}`}
        title={parcelIds}
        titleVariant="paragraph-m-bold"
        description={
          <>
            <Typography variant="paragraph-s" color="grey-600">
              {getDocFieldValues(candidate.docFieldRaw, (f) => f.field_number).join(", ")}
            </Typography>
            <Typography variant="paragraph-s" color="grey-600" className="margin-left-50">
              {getDocFieldValues(candidate.docFieldRaw, (f) => f.field_size)
                .map((size) => `${formatNumber({ number: parseFloat(size) })} ha`)
                .join(", ") || "-"}
            </Typography>
            <Typography variant="paragraph-s" color="grey-600" className="margin-left-50">
              {getDocFieldCrops(candidate.docFieldRaw)
                .map((c) => c.crop_name)
                .join(", ")}
            </Typography>
          </>
        }
        tag={isCurrent ? { text: t("fieldMatchCard.current"), colour: "grey" } : undefined}
        checked={checked}
        onChange={onChange}
        selectionMode="single"
      />
    </div>
  );
};

const FieldCard = ({
  record,
  overriddenDocField,
  overriddenMatchId,
  isSelected,
  onToggle,
  onChangeMatch,
  docScanId,
  isPoland,
  t,
}: {
  record: DocScanFieldRecord;
  overriddenDocField: DocFieldRaw | null;
  overriddenMatchId: string | null;
  isSelected: boolean;
  onToggle: () => void;
  onChangeMatch: (newMatch: DocScanFieldRecord) => void;
  docScanId: string;
  isPoland?: boolean;
  t: TFunction;
}) => {
  const [showChangeField, setShowChangeField] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const debouncedSearch = useDebouncedState(searchInput, 300);
  const isOverridden = !!overriddenDocField;
  const currentMatchId = overriddenMatchId ?? record.id;
  const effectiveRecord = overriddenDocField ? { ...record, docFieldRaw: overriddenDocField } : record;
  const [selectedRecord, setSelectedRecord] = useState<DocScanFieldRecord>(record);
  const userHasSelected = useRef(false);

  const { loadedFields, isFetching, sentinelRef } = useDocScanFieldsPaginated({
    docScanId,
    pinRecord: record,
    enabled: showChangeField,
    excludeOverallStatus: "missing_data",
    search: debouncedSearch || undefined,
  });

  // Sync selectedRecord with override state, but not if user has already picked
  useEffect(() => {
    if (userHasSelected.current) return;

    if (overriddenMatchId) {
      const match = loadedFields.find((f) => f.id === overriddenMatchId);
      if (match) setSelectedRecord(match);
    } else {
      setSelectedRecord(record);
    }
  }, [overriddenMatchId, loadedFields, record]);

  const handleCancel = () => {
    setShowChangeField(false);
    setSearchInput("");
    userHasSelected.current = false;
    setSelectedRecord(loadedFields.find((f) => f.id === currentMatchId) ?? record);
  };

  const handleSave = () => {
    setShowChangeField(false);
    setSearchInput("");
    userHasSelected.current = false;
    onChangeMatch(selectedRecord);
  };

  const MatchCard = isPoland ? PLFieldMatchCard : FieldMatchCard;

  return (
    <MatchCard
      record={effectiveRecord}
      isSelected={isSelected}
      onToggle={onToggle}
      onChangeField={() => setShowChangeField((prev) => !prev)}
      className={isPoland ? undefined : "margin-bottom-125"}
      hideStatuses={isOverridden}
      headerActions={
        showChangeField ? (
          <>
            <Button type="secondary" onClick={handleCancel} dataTestId="field-picker-cancel">
              {t("fieldMatchCard.cancel")}
            </Button>
            <Button type="primary" onClick={handleSave} dataTestId="field-picker-save">
              {t("fieldMatchCard.save")}
            </Button>
          </>
        ) : undefined
      }
      rightContent={
        showChangeField && (
          <>
            <InputSearch
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              label={t("fieldMatchCard.searchPlaceholder")}
              className="margin-bottom-50"
              dataTestId="field-picker-search"
            />
            {loadedFields.map((r) =>
              isPoland ? (
                <PLFieldPickerRow
                  key={r.id}
                  candidate={r}
                  isCurrent={currentMatchId === r.id}
                  checked={selectedRecord.id === r.id}
                  onChange={() => {
                    userHasSelected.current = true;
                    setSelectedRecord(r);
                  }}
                  t={t}
                />
              ) : (
                <SelectableImageCard
                  key={r.id}
                  dataTestId={`field-picker-card-${r.id}`}
                  title={r.docFieldRaw?.field_name ?? "-"}
                  titleVariant="paragraph-m-bold"
                  description={
                    <>
                      <Typography variant="paragraph-s" color="grey-600">
                        {r.docFieldRaw?.field_number}
                      </Typography>
                      <Typography variant="paragraph-s" color="grey-600" className="margin-left-50">
                        {r.docFieldRaw?.flik_number}
                      </Typography>
                      <Typography variant="paragraph-s" color="grey-600" className="margin-left-50">
                        {r.docFieldRaw?.field_size ? `${formatNumber({ number: parseFloat(r.docFieldRaw.field_size) })} ha` : "-"}
                      </Typography>
                      <Typography variant="paragraph-s" color="grey-600" className="margin-left-50">
                        {getDocFieldCrops(r.docFieldRaw)
                          .map((c) => c.crop_name)
                          .join(", ")}
                      </Typography>
                    </>
                  }
                  tag={currentMatchId === r.id ? { text: t("fieldMatchCard.current"), colour: "grey" } : undefined}
                  checked={selectedRecord.id === r.id}
                  onChange={() => {
                    userHasSelected.current = true;
                    setSelectedRecord(r);
                  }}
                  selectionMode="single"
                />
              )
            )}

            {isFetching && <PageSpin />}

            <div ref={sentinelRef} style={{ height: 8 }} />
          </>
        )
      }
    />
  );
};
